/**
 * ============================================================================
 * Specifikit Pre-order Feature
 * Location: assets/specifikit-preorder.js
 * Version: 1.2 (Logger Integration)
 * Author: specifikit.com
 * ============================================================================
 * This module adds robust, optional pre-order functionality. It activates
 * based on product metafields or inventory status, provides a superior UI,
 * and ensures correct data is passed to the cart.
 * ============================================================================
 */

class SpecifikitPreorder {
  constructor(customizerInstance) {
    this.customizer = customizerInstance;
    this.config = this.customizer.config.preorderConfig;
    // Inherit the logger from the core instance for unified, conditional logging.
    // Failsafe: Fall back to the native console if the logger is not available.
    this.logger = customizerInstance.logger || console;

    this.logger.group('📦 Specifikit Pre-order | Client-Side Module');
    this.logger.log('Initializing...');

    if (!this.config || !this.config.enabled) {
      this.logger.log('Feature is DISABLED globally in theme settings. Module will not run.');
      this.logger.groupEnd();
      return;
    }

    this.logger.log('Configuration received from server:', this.config);
    this.logger.groupEnd();

    this.elements = {};
    this.isCurrentVariantPreorder = false;
    this.originalButtonText = '';

    // Defer initialization until the main UI elements are ready.
    requestAnimationFrame(() => {
      this.init();
    });
  }

  init() {
    this.queryElements();
    if (!this.elements.submitButton || !this.elements.messageContainer || !this.elements.preorderPropertyInput) {
      this.logger.error('Pre-order: Missing critical UI elements. Required: [data-specifikit-submit-button], [data-specifikit-preorder-message-container], [data-specifikit-preorder-prop]. Aborting.');
      return;
    }

    // Store the original "Add to Cart" text to allow reverting.
    this.originalButtonText = this.elements.submitButtonText?.textContent.trim() || 'Add to Cart -';

    this.attachListeners();

    // Perform an initial check on load to synchronize with server state and metafields.
    this.checkPreorderStatus(this.customizer.state);
  }

  queryElements() {
    const scope = this.customizer.container;
    this.elements = {
      submitButton: scope.querySelector('[data-specifikit-submit-button]'),
      submitButtonText: scope.querySelector('[data-specifikit-submit-button] .specifikit-btn-text'),
      messageContainer: scope.querySelector('[data-specifikit-preorder-message-container]'),
      preorderPropertyInput: scope.querySelector('[data-specifikit-preorder-prop]'),
    };
  }

  attachListeners() {
    // Listen for state updates from the core to re-evaluate pre-order status on variant changes or inventory loads.
    this.customizer.container.addEventListener('specifikit:stateUpdated', (e) => this.checkPreorderStatus(e.detail.state));
  }

  checkPreorderStatus(state) {
    this.logger.groupCollapsed(`📦 Pre-order | Status Check @ ${new Date().toLocaleTimeString()}`);

    const productConfig = this.config.productConfig || {};
    
    // ★★★ ROBUST MODE PARSING WITH MULTI-LAYERED FAILSAFE ★★★
    let rawMode = productConfig.mode;
    this.logger.log(`- Raw Product Mode Received:`, rawMode, `(Type: ${typeof rawMode})`);
    
    let normalizedMode = 'disabled'; // Safe default
    if (rawMode !== null && rawMode !== undefined && rawMode !== '') {
      normalizedMode = String(rawMode).trim().toLowerCase();
    }
    
    this.logger.log(`- Normalized Mode: "${normalizedMode}"`);
    this.logger.log(`- Current Variant ID: ${state.variantId}`);

    const allowedModes = ['automatic', 'always', 'disabled'];
    if (!allowedModes.includes(normalizedMode)) {
      this.logger.warn(`-> Invalid pre-order mode: "${rawMode}". Allowed values are: ${allowedModes.join(', ')}. Defaulting to 'disabled'.`);
      normalizedMode = 'disabled';
    }

    if (normalizedMode === 'disabled') {
      this.isCurrentVariantPreorder = false;
      this.logger.log('-> Decision: Mode is "disabled". Result: isPreorder = false.');
      this.logger.groupEnd();
      this.updateUI(false, '', '');
      return;
    }

    let isPreorder = false;
    let leadTime = productConfig.lead_time || '';

    if (normalizedMode === 'always') {
      isPreorder = true;
      this.logger.log("-> Decision: Mode is 'always'. Result: isPreorder = true.");
    }
    else if (normalizedMode === 'automatic') {
      this.logger.log("-> Decision: Mode is 'automatic'. Checking inventory...");
      if (!this.customizer.liveProductData || !this.customizer.liveProductData.variants) {
        this.logger.warn('   - Live inventory data not yet available. Pre-order status for "automatic" mode cannot be confirmed. Waiting for inventory fetch to complete.');
        this.logger.groupEnd();
        return;
      }

      const currentVariantId = parseInt(state.variantId, 10);
      const currentVariantData = this.customizer.liveProductData.variants.find(v => v.id === currentVariantId);

      if (currentVariantData) {
        this.logger.log('   - Variant Inventory:', {
          management: currentVariantData.inventory_management,
          quantity: currentVariantData.inventory_quantity,
          policy: currentVariantData.inventory_policy
        });

        const isOversellable = currentVariantData.inventory_policy === 'continue';
        const isOutOfStock = currentVariantData.inventory_quantity <= 0;
        const isTracked = currentVariantData.inventory_management === 'shopify';

        if (isTracked && isOutOfStock && isOversellable) {
          isPreorder = true;
          this.logger.log('   - SUCCESS: Inventory meets automatic pre-order conditions. Result: isPreorder = true.');
        } else {
          this.logger.log('   - INFO: Inventory is in stock or not configured for pre-order. Result: isPreorder = false.');
        }
      } else {
        this.logger.warn(`   - FAILED: Could not find live data for variant ID ${currentVariantId}. Pre-order status cannot be determined.`);
      }
    }

    let baseMessage = '';
    if (isPreorder) {
      baseMessage = productConfig.message ? productConfig.message.trim() : this.config.defaultMessage;
    }

    this.isCurrentVariantPreorder = isPreorder;
    this.logger.log('-> Final Decision: isPreorder =', isPreorder);
    this.logger.groupEnd();
    this.updateUI(isPreorder, baseMessage, leadTime);
  }

  updateUI(isPreorder, baseMessage, leadTime) {
    if (!this.elements.submitButtonText || !this.elements.messageContainer || !this.elements.preorderPropertyInput) {
      this.logger.error("Pre-order: Cannot update UI because one or more critical elements are missing.");
      return;
    }

    this.logger.groupCollapsed(`📦 Pre-order | Updating UI (isPreorder: ${isPreorder})`);

    if (isPreorder) {
      // Update UI for Pre-order state
      this.elements.submitButtonText.textContent = this.config.buttonText + ' -';
      this.logger.log(`  - Setting button text to: "${this.config.buttonText}"`);

      const iconShape = this.config.iconShape || 'none';

      let leadTimeHTML = '';
      if (leadTime) {
        const formattedLeadTimeMessage = this.config.leadTimeFormat.replace('{lead_time}', `<strong>${this.customizer.ui.escapeHtml(leadTime)}</strong>`);
        leadTimeHTML = `
          <div class="specifikit-preorder-message__item">
            <div class="specifikit-preorder-message__icon" data-icon-shape="${iconShape}">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clip-rule="evenodd" />
              </svg>
            </div>
            <div class="specifikit-preorder-message__text">${formattedLeadTimeMessage}</div>
          </div>
        `;
      }

      const newHTML = `
        <div class="specifikit-preorder-message__content">
          <div class="specifikit-preorder-message__item">
            <div class="specifikit-preorder-message__icon" data-icon-shape="${iconShape}">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
              </svg>
            </div>
            <div class="specifikit-preorder-message__text">${this.customizer.ui.escapeHtml(baseMessage).replace(/\n/g, '<br>')}</div>
          </div>
          ${leadTimeHTML}
        </div>
      `;

      if (this.elements.messageContainer.innerHTML.replace(/\s/g, '') !== newHTML.replace(/\s/g, '')) {
        this.elements.messageContainer.innerHTML = newHTML;
        this.logger.log('  - Updating message content.');
      }
      
      this.elements.messageContainer.classList.add('is-visible');
      this.logger.log('  - Showing message container.');

      this.elements.preorderPropertyInput.value = leadTime;
      this.logger.log(`  - Setting lead time property to: "${leadTime}"`);

    } else {
      // Revert UI to standard Add to Cart state
      if (this.elements.submitButtonText.textContent.trim() !== this.originalButtonText.trim()) {
        this.elements.submitButtonText.textContent = this.originalButtonText;
        this.logger.log(`  - Reverting button text to: "${this.originalButtonText}"`);
      }

      this.elements.messageContainer.classList.remove('is-visible');
      this.logger.log('  - Hiding message container.');

      if (this.elements.messageContainer.innerHTML !== '') {
        this.elements.messageContainer.innerHTML = '';
      }

      this.elements.preorderPropertyInput.value = '';
      this.logger.log('  - Clearing lead time property.');
    }
    this.logger.groupEnd();
  }
}

document.addEventListener('specifikitCore:loaded', (e) => {
  try {
    if (window.specifikitPreorder) return;
    window.specifikitPreorder = new SpecifikitPreorder(e.detail.instance);
  } catch (error) {
    // The logger may not be available if the core instance itself failed, so a native error is a safe fallback.
    const logger = e.detail?.instance?.logger || console;
    logger.error('Critical error initializing pre-order feature:', error);
  }
});

/**
 * ============================================================================
 * END Specifikit Pre-order Feature JS FILE
 * ============================================================================
 */